import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useCommonItems} from "../../../../contexts/CommonItensProvider";
import {useSearchParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {atualizaSistema, deletaSistema, listaSistemas} from "../configs/functions";
import {Helmet} from "react-helmet-async";
import {
    Breadcrumbs,
    Button,
    CircularProgress,
    Grid, IconButton,
    Paper,
    Stack, Switch, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
    TextField,
    Typography
} from "@mui/material";
import LinkNavegacao from "../../../common/Link";
import {EditIcon, HomeIcon, SystemInformationIcon, TrashIcon} from "../../../common/Icons";
import PageTitle from "../../../common/PageTitle";
import {Add, FilterAlt, Restore} from "@mui/icons-material";
import {Controller, useForm} from "react-hook-form";
import {getFormErrorMessage} from "../../../../configs/functions";
import {SkeletonTableRow} from "../../../common/SkeletonTableRow";
import {EmptyTableRow} from "../../../common/EmptyTableRow";
import {Pagination} from "../../../common/Pagination";
import {SystemForm} from "./forms/SystemForm";
import ConfirmDialog from "../../../common/ConfirmDialog";

const SystemRow = ({
                        data,
                        handleOpenEditForm,
                        handleUpdateStatus,
                        handleOpenDeleteDialog,
}) => {
    return (
        <>
            <TableRow hover>
                <TableCell>{data.nome}</TableCell>
                <TableCell>{data.descricao}</TableCell>
                <TableCell>
                    <Switch
                        checked={!!data.status}
                        onChange={handleUpdateStatus}
                    />
                </TableCell>
                <TableCell sx={{ minWidth: 50 }}>
                    <IconButton aria-label="Editar" onClick={() => handleOpenEditForm(data)}>
                        <EditIcon />
                    </IconButton>

                    <IconButton aria-label="Excluir" onClick={() => handleOpenDeleteDialog(data)}>
                        <TrashIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
        </>
    );
}

export function ListOfSystems() {
    const { exibirAlerta } = useCommonItems();

    const queryClient = useQueryClient();

    const [searchParams, setSearchParams] = useSearchParams();

    const nome = searchParams.get('nome');
    const perPage = searchParams.get('perPage');

    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    // estados para controle de paginacao
    const page = searchParamPage ? (parseInt(searchParamPage) - 1) : 0;
    const rowsPerPage = searchParamPerPage ? parseInt(searchParamPerPage) : 10;

    const [selectedSystem, setSelectedSystem] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [deleteOpenDialog, setOpenDeleteDialog] = useState(false);

    const { handleSubmit, reset, control } = useForm({
        defaultValues: {
            nome: nome ?? '',
        },
    });

    function handleSubmitFilter(data) {
        for (const key in data) {
            // remove campos vazios
            if (data[key] === '') {
                delete data[key];
            }
        }

        const queryFilters = {
            ...data,
            page: '1',
            perPage: perPage ?? '10'
        }

        // fill url params with filters
        setSearchParams(queryFilters);
    }

    const queryFilters = {
        nome: searchParams.get('nome'),
        limit: rowsPerPage,
        offset: page * rowsPerPage,
    };

    const queryKey = ['listaSistemas', queryFilters];
    const { data: listaSystemsData, isPending: listaSystemsDataIsLoading } = useQuery({
        queryKey,
        queryFn: () => listaSistemas(queryFilters),
    });

    const systems = listaSystemsData?.data?.data ?? [];
    const numTotalItems = listaSystemsData?.data?.data?.numero_total ?? 0;

    const handleEdit = (item) => {
        setSelectedSystem(item);
        setOpenForm(true);
    };

    const handleOpenDeleteDialog = (item) => {
        setSelectedSystem(item);
        setOpenDeleteDialog(true);
    };

    const handleUpdateStatus = useMutation({
        mutationFn: (data) => atualizaSistema(data.id, data.dataToUpdate),
        onSuccess: (response) => {
            const message = response?.data?.message ?? 'Status atualizado com sucesso';
            exibirAlerta('Sucesso', message, 'success');
            queryClient.invalidateQueries({ queryKey });
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        }
    });

    const { mutateAsync: handleDelete } = useMutation({
        mutationFn: deletaSistema,
        onSuccess: (response, id) => {
            const message = response.data?.message ?? 'Sistema excluído com sucesso';
            exibirAlerta('Sucesso', message, 'success');

            const cached = queryClient.getQueryData(queryKey);
            const updatedData = {
                ...cached,
                data: {
                    ...cached.data,
                    data: cached.data.data.filter((item) => item.id !== id),
                }
            };
            queryClient.setQueryData(queryKey, updatedData);
        },
        onError: (error) => {
            const message = error.response?.data.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        },
        onSettled: () => {
            setOpenDeleteDialog(false);
            setSelectedSystem(null);
        }
    });

    useEffect(() => {
        setSearchParams((state) => {
            state.set('page', String(page + 1));
            state.set('perPage', String(rowsPerPage));

            return state;
        });
    }, [page, rowsPerPage, setSearchParams]);

    useEffect(() => {
        const queryFilters = {
            ...Object.fromEntries(searchParams),
        }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

    }, [searchParams]);

    return (
        <>
            <Helmet title="Sistemas" defer={false} />

            <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={3}>
                <Grid item xs={12} md={9}>
                    <Breadcrumbs>
                        <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao>
                        <Typography variant='span'>Admin Sempher</Typography>
                        <LinkNavegacao to='/admin/operacional'>Gestão Operacional</LinkNavegacao>
                        <Typography variant='span'>Gerenciamento de Sistemas</Typography>
                    </Breadcrumbs>

                    <PageTitle
                        icon={<SystemInformationIcon fontSize='large' />}
                        title="Gerenciamento de Sistemas"
                        description="Gerencie os sistemas oferecidos pela ASW/Sempher Solutions"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Stack direction={{ xs: 'column-reverse', md: 'row' }} spacing={2} justifyContent={'space-between'} alignItems={'center'}>
                        <Grid item xs={12} md={8} >
                            <Grid container spacing={2} alignItems="center" component="form" onSubmit={handleSubmit(handleSubmitFilter)}>
                                <Grid item md={6}>
                                    <Controller
                                        name="nome"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Nome"
                                                size='small'
                                                InputProps={{ style: { backgroundColor: '#fff' } }}

                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item md={4}>
                                    <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} justifyContent={'space-between'}>
                                        <Button
                                            fullWidth
                                            type='submit'
                                            color='primary'
                                            variant='contained'
                                            disabled={listaSystemsDataIsLoading}
                                            startIcon={listaSystemsDataIsLoading ? <CircularProgress size={16} sx={{ color: 'textSecondary' }} /> : <FilterAlt />}
                                        >
                                            Filtrar
                                        </Button>

                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<Restore />}
                                            onClick={() => {
                                                reset();
                                                setSearchParams({
                                                    page: '1',
                                                    perPage: perPage ?? '10'
                                                });
                                            }}
                                        >
                                            Limpar Filtros
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Typography sx={{ textAlign: { xs: 'center', md: 'right' }, mb: 1 }}>
                            <Button variant="contained" startIcon={<Add />} onClick={() => setOpenForm(true)}>
                                Cadastrar Sistema
                            </Button>
                        </Typography>
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table aria-label="Sistemas">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {listaSystemsDataIsLoading ? (
                                    <SkeletonTableRow numCells={4} numRows={rowsPerPage < 20 ? rowsPerPage : 5} />
                                ) : systems.length > 0 ? (
                                    systems.map((item) => (
                                        <SystemRow
                                            key={item.id}
                                            data={item}
                                            handleUpdateStatus={() => {
                                                const dataSendUpdate = { id: item.id, dataToUpdate: { status: !item.status } }
                                                handleUpdateStatus.mutateAsync(dataSendUpdate)
                                            }}
                                            handleOpenEditForm={() => handleEdit(item)}
                                            handleOpenDeleteDialog={() => handleOpenDeleteDialog(item)}
                                        />
                                    ))
                                ) : (
                                    <EmptyTableRow colSpan={4} />
                                )}
                            </TableBody>
                        </Table>

                        <Pagination
                            numTotalItems={numTotalItems}
                            rowsPerPage={rowsPerPage}
                            page={page}
                        />
                    </TableContainer>
                </Grid>
            </Grid>

            <SystemForm
                open={openForm}
                setOpen={(value) => {
                    setOpenForm(value);
                    if (!value) setSelectedSystem(null);
                }}
                system={selectedSystem}
                queryKey={queryKey}
            />

            <ConfirmDialog
                description="Tem certeza que deseja excluir esse Sistema?"
                title="Excluir Sistema"
                goAction={() => handleDelete(selectedSystem.id)}
                handleClose={() => {
                    setOpenDeleteDialog(false);
                    setSelectedSystem(null);
                }}
                state={deleteOpenDialog}
            />
        </>
    );
}